// PropertyTab — Ship 5c (Apr 30, 2026)
// Renders the storm_reports row attached to a deal. Auto-generates the PDF
// (via dynamic-imported pdf-lib) the first time an RM opens this tab on a
// report that doesn't have a pdf_storage_path yet. Manual "Regenerate"
// button calls storm-report-prepare with regenerate=true to refresh the
// data_snapshot and then re-renders the PDF.
//
// Future: hero map (Mapbox), parcel data (Regrid), knock history.

import { useEffect, useState } from "react";
import { supabase, SUPABASE_URL } from "../../lib/supabase";
import { fetchEdge } from "../../lib/edgeError";
import type { Deal } from "../../lib/types";
import PropertyProfileSection from "./PropertyProfileSection";
import KnockHistorySection from "./KnockHistorySection";
import PropertyHeroMap from "./PropertyHeroMap";
import { mapsHref, isAddressLinkable } from "../../lib/mapsHref";

interface StormReportRow {
  id: string;
  short_id: string;
  storm_date: string;
  property_max_in: number | null;
  vicinity_max_in: number | null;
  spotter_max_in: number | null;
  radar_signature_count: number | null;
  spotter_report_count: number | null;
  pdf_storage_path: string | null;
  generated_at: string;
  data_snapshot: any;
}

interface Props {
  deal: Deal;
}

export default function PropertyTab({ deal }: Props) {
  const [reports, setReports] = useState<StormReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [renderingId, setRenderingId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function loadReports() {
    setLoading(true);
    const { data, error } = await supabase
      .from("storm_reports")
      .select("id, short_id, storm_date, property_max_in, vicinity_max_in, spotter_max_in, radar_signature_count, spotter_report_count, pdf_storage_path, generated_at, data_snapshot")
      .eq("deal_id", deal.id)
      .order("generated_at", { ascending: false });
    if (error) setErr(error.message);
    setReports((data ?? []) as StormReportRow[]);
    setLoading(false);
  }

  useEffect(() => { loadReports(); }, [deal.id]);

  // Auto-render any storm_reports row that has data but no PDF yet
  useEffect(() => {
    const pending = reports.find((r) => !r.pdf_storage_path && r.data_snapshot);
    if (!pending) return;
    if (renderingId === pending.id) return;
    renderAndUpload(pending);
  }, [reports]);

  async function renderAndUpload(row: StormReportRow) {
    setRenderingId(row.id);
    setErr(null);
    try {
      const { renderStormReportPDF } = await import("../../lib/stormReportPDF");
      const data = { ...row.data_snapshot, short_id: row.short_id };
      const bytes = await renderStormReportPDF(data);
      const path = `storm-reports/${deal.id}/${Date.now()}.pdf`;
      const { error: upErr } = await supabase.storage
        .from("deal-documents")
        .upload(path, bytes, { contentType: "application/pdf", upsert: true });
      if (upErr) throw upErr;
      const { error: updErr } = await supabase
        .from("storm_reports")
        .update({ pdf_storage_path: path })
        .eq("id", row.id);
      if (updErr) throw updErr;
      // Reload
      await loadReports();
    } catch (e: any) {
      setErr(e.message ?? String(e));
    } finally {
      setRenderingId(null);
    }
  }

  async function regenerate() {
    if (!deal.id) return;
    setGenerating(true);
    setErr(null);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session.session?.access_token;
      const resp = await fetchEdge(
        `${SUPABASE_URL}/functions/v1/storm-report-prepare`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          // Oct 1, 2026: a manual generate also files the PDF into Documents
          // (storm-report-prepare v5 renders server-side). Archived reports
          // are frozen, so re-pressing for the same storm is a no-op.
          body: JSON.stringify({ deal_id: deal.id, regenerate: true, archive: true, archive_reason: "manual" }),
        }
      );
      const body = await resp.json();
      if (!resp.ok) throw new Error(body.error ?? `HTTP ${resp.status}`);
      if (!body.generated) {
        setErr(body.reason === "already_archived"
          ? "This storm's report is already saved in Documents."
          : body.message ?? body.reason ?? "No storm in window for this property.");
      }
      await loadReports();
    } catch (e: any) {
      setErr(e.message ?? String(e));
    } finally {
      setGenerating(false);
    }
  }

  async function openPdf(path: string) {
    const { data, error } = await supabase.storage
      .from("deal-documents")
      .createSignedUrl(path, 3600);
    if (error || !data?.signedUrl) {
      setErr(error?.message ?? "Could not open PDF.");
      return;
    }
    window.open(data.signedUrl, "_blank");
  }

  const headlineReport = reports[0];

  if (loading) {
    return <div className="p-4 text-sm text-slate-500">Loading property intelligence…</div>;
  }

  return (
    <div className="space-y-5 p-4">
      {/* Hero map — Mapbox embed with property pin + 1-mi storm radius + radar/spotter dots */}
      <PropertyHeroMap deal={deal} />

      {/* ── Header strip ────────────────────────────────────────────── */}
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Property</div>
            <div className="mt-1 text-base font-semibold text-slate-900">
              {isAddressLinkable(deal) ? (
                <a
                  href={mapsHref(deal)}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:underline hover:text-slate-700"
                >
                  {deal.property_street}
                </a>
              ) : (
                deal.property_street ?? "—"
              )}
            </div>
            <div className="text-sm text-slate-600">
              {[deal.property_city, deal.property_state, deal.property_zip].filter(Boolean).join(", ")}
            </div>
            {deal.property_lat && deal.property_lon ? (
              <div className="mt-1 font-mono text-xs text-slate-400">
                {Number(deal.property_lat).toFixed(5)}, {Number(deal.property_lon).toFixed(5)}
              </div>
            ) : (
              <div className="mt-2 rounded-md bg-amber-50 p-2 text-xs text-amber-800">
                No property coordinates on this deal — storm reports require lat/lon. Add via the Overview tab.
              </div>
            )}
          </div>
          <button
            onClick={regenerate}
            disabled={!deal.property_lat || !deal.property_lon || generating}
            className="shrink-0 rounded-md bg-orange-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {generating ? "Generating…" : "📄 Generate weather report"}
          </button>
        </div>
        {err ? (
          <div className="mt-3 rounded-md bg-red-50 p-2 text-xs text-red-700">⚠ {err}</div>
        ) : null}
      </div>

      {/* Property Profile (parcel data) */}
      <PropertyProfileSection dealId={deal.id} />

      {/* ── Headline storm summary ───────────────────────────────── */}
      {headlineReport ? (
        <div className="rounded-lg border border-slate-200 bg-white">
          <div className="border-b border-slate-200 bg-slate-50 px-4 py-2.5">
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Headline Storm Event
              </div>
              <div className="font-mono text-xs text-slate-400">{headlineReport.short_id}</div>
            </div>
          </div>
          <div className="p-4">
            <div className="text-lg font-semibold text-slate-900">
              Storm event on {fmtStormDate(headlineReport.storm_date)}
            </div>
            <div className="mt-3 grid grid-cols-3 gap-3">
              <Stat
                label="At the property"
                value={fmtSize(headlineReport.property_max_in)}
                hint={`Within ${(headlineReport.data_snapshot?.generated_at ?? headlineReport.generated_at ?? "") >= "2026-09-30T16:36:00Z" ? "1 mile" : "1.5 mi"} · NEXRAD`}
                accent={headlineReport.property_max_in ? "orange" : "slate"}
              />
              <Stat
                label="Storm corridor"
                value={fmtSize(headlineReport.data_snapshot?.headline?.corridor_max_in ?? headlineReport.vicinity_max_in)}
                hint="≤10 mi · same date"
                accent="orange"
              />
              <Stat
                label="NWS spotters"
                value={
                  (headlineReport.spotter_report_count ?? 0) > 0
                    ? `${headlineReport.spotter_report_count}`
                    : "—"
                }
                hint="Human-confirmed"
                accent={(headlineReport.spotter_report_count ?? 0) > 0 ? "teal" : "slate"}
              />
            </div>

            {/* Larger-hail callout */}
            {headlineReport.data_snapshot?.larger_hail_callout ? (
              <div className="mt-4 rounded-md border border-red-300 bg-red-50 p-3">
                <div className="text-xs font-semibold uppercase tracking-wider text-red-700">
                  ⚠ Larger hail in the same storm
                </div>
                <div className="mt-1 text-sm font-medium text-slate-900">
                  {headlineReport.data_snapshot.larger_hail_callout.size_in.toFixed(2)}″ {String(headlineReport.data_snapshot.larger_hail_callout.descriptor).toLowerCase()}-size hail · {headlineReport.data_snapshot.larger_hail_callout.distance_mi} mi {headlineReport.data_snapshot.larger_hail_callout.bearing} of property
                </div>
                <div className="mt-1 text-xs text-slate-600">
                  ~{headlineReport.data_snapshot.larger_hail_callout.damage_potential_ratio}× damage potential vs. hail at the property.
                </div>
              </div>
            ) : null}

            {/* Notable secondary event */}
            {headlineReport.data_snapshot?.secondary_event ? (
              <div className="mt-4 rounded-md border border-orange-200 bg-orange-50 p-3">
                <div className="text-xs font-semibold uppercase tracking-wider text-orange-700">
                  ★ Notable secondary event
                </div>
                <div className="mt-1 text-sm font-medium text-slate-900">
                  {fmtStormDate(headlineReport.data_snapshot.secondary_event.date)} — {headlineReport.data_snapshot.secondary_event.size_in.toFixed(2)}″ {String(headlineReport.data_snapshot.secondary_event.descriptor).toLowerCase()}-size hail
                </div>
                <div className="mt-1 text-xs text-slate-600">
                  {headlineReport.data_snapshot.secondary_event.distance_mi} mi {headlineReport.data_snapshot.secondary_event.bearing} of property · {fmtDaysOffset(headlineReport.data_snapshot.secondary_event.days_offset)}
                </div>
              </div>
            ) : null}

            {/* PDF link */}
            <div className="mt-4 flex items-center gap-3 border-t border-slate-200 pt-3">
              {headlineReport.pdf_storage_path ? (
                <button
                  onClick={() => openPdf(headlineReport.pdf_storage_path!)}
                  className="rounded-md bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-700"
                >
                  📄 Open PDF
                </button>
              ) : renderingId === headlineReport.id ? (
                <div className="text-sm text-slate-500">Rendering PDF…</div>
              ) : (
                <button
                  onClick={() => renderAndUpload(headlineReport)}
                  className="rounded-md bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200"
                >
                  Render PDF now
                </button>
              )}
              <a
                href={`https://lxymewxomybjdrmgchqt.supabase.co/functions/v1/storm-report-verify?id=${headlineReport.short_id}`}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-orange-600 hover:underline"
              >
                Public verify page →
              </a>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
          <div className="text-sm font-medium text-slate-700">No storm report yet for this property.</div>
          <div className="mt-1 text-xs text-slate-500">
            Click <strong>Generate weather report</strong> to pull NOAA radar + NWS spotter data within 15 mi for the last 365 days.
          </div>
        </div>
      )}

      {/* ── 12-month context ────────────────────────────────────── */}
      {headlineReport?.data_snapshot?.year_context ? (
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            12-Month Pattern of Storm Activity
          </div>
          <div className="mt-3 grid grid-cols-4 gap-3">
            <Stat
              label="Storm dates"
              value={String(headlineReport.data_snapshot.year_context.distinct_storm_dates)}
              hint="≥1″ days within 15 mi"
              accent="teal"
            />
            <Stat
              label="Total radar hits"
              value={String(headlineReport.data_snapshot.year_context.total_signatures)}
              hint="≥1.0″ signatures"
              accent="teal"
            />
            <Stat
              label="Largest hail"
              value={fmtSize(headlineReport.data_snapshot.year_context.max_size_in)}
              hint="Anywhere within 15 mi"
              accent="orange"
            />
            <Stat
              label="Spotter reports"
              value={String(headlineReport.data_snapshot.year_context.total_spotters)}
              hint="Human-confirmed"
              accent="teal"
            />
          </div>
        </div>
      ) : null}

      {/* ── All reports ─────────────────────────────────────────── */}
      {reports.length > 1 ? (
        <div className="rounded-lg border border-slate-200 bg-white">
          <div className="border-b border-slate-200 bg-slate-50 px-4 py-2.5">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              All storm reports for this property
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {reports.map((r) => (
              <div key={r.id} className="flex items-center justify-between px-4 py-2.5">
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-slate-900">
                    {fmtStormDate(r.storm_date)}
                  </span>
                  <span className="font-mono text-xs text-slate-400">{r.short_id}</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-slate-600">
                  <span>{fmtSize(r.property_max_in ?? r.vicinity_max_in)} max</span>
                  <span>·</span>
                  <span>{r.radar_signature_count ?? 0} radar / {r.spotter_report_count ?? 0} spotters</span>
                  {r.pdf_storage_path ? (
                    <button
                      onClick={() => openPdf(r.pdf_storage_path!)}
                      className="rounded-md border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                    >
                      Open PDF
                    </button>
                  ) : (
                    <button
                      onClick={() => renderAndUpload(r)}
                      disabled={renderingId === r.id}
                      className="rounded-md bg-slate-900 px-2 py-1 text-xs font-semibold text-white hover:bg-slate-700 disabled:opacity-50"
                    >
                      {renderingId === r.id ? "Rendering…" : "Render"}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Knock & Contact History */}
      <KnockHistorySection knockPinId={(deal as any).knock_pin_id ?? null} />
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────
function fmtStormDate(d: string): string {
  if (!d) return "—";
  // Use noon UTC so ET (UTC-4/-5) doesn't roll the date back
  return new Date(d + "T12:00:00Z").toLocaleDateString("en-US", {
    month: "long", day: "numeric", year: "numeric",
  });
}

function fmtSize(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  return `${Number(n).toFixed(2)}″`;
}

function fmtDaysOffset(d: number): string {
  const abs = Math.abs(d);
  if (d < 0) return `${abs} days before headline`;
  if (d > 0) return `${abs} days after headline`;
  return "same day";
}

function Stat({
  label, value, hint, accent,
}: { label: string; value: string; hint: string; accent: "orange" | "teal" | "slate" }) {
  const valueColor =
    accent === "orange" ? "text-orange-600" :
    accent === "teal" ? "text-teal-700" :
    "text-slate-400";
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div className={`mt-1 text-2xl font-bold ${valueColor}`}>{value}</div>
      <div className="mt-1 text-[10px] text-slate-500">{hint}</div>
    </div>
  );
}
